import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Fade,
  Grid,
  IconButton,
  Modal,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import { useNavigate } from "react-router-dom";
import SidebarMenu from "../AdminPanel/SidebarMenu";
import TopBar from "../AdminPanel/TopBar";

const API_BASE =
  process.env.REACT_APP_API_URL ||
  process.env.REACT_APP_API_BASE_URL ||
  "";

const tableHeadSx = {
  bgcolor: "#1565C0",
  color: "#fff",
  fontWeight: 700,
  whiteSpace: "nowrap",
  py: 1.15,
  fontSize: 14,
};

const modalBoxSx = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "92%", sm: "80%", md: 620 },
  maxHeight: "90vh",
  bgcolor: "#fff",
  borderRadius: 3,
  boxShadow: "0 24px 60px rgba(15,23,42,0.28)",
  overflow: "hidden",
};

const emptyForm = {
  codigo: "",
  nombre: "",
  establecimiento_id: "",
};

function getCSRFToken() {
  const name = "csrftoken";
  const cookies = document.cookie.split(";");

  for (let cookie of cookies) {
    const trimmed = cookie.trim();
    if (trimmed.startsWith(name + "=")) {
      return decodeURIComponent(trimmed.substring(name.length + 1));
    }
  }

  return "";
}

const getToken = () => {
  return (
    localStorage.getItem("access") ||
    localStorage.getItem("access_token") ||
    localStorage.getItem("token") ||
    ""
  );
};

const authHeaders = () => {
  const token = getToken();
  const csrf = getCSRFToken();

  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(csrf ? { "X-CSRFToken": csrf } : {}),
  };
};

const normalizeBodegas = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.bodegas)) return data.bodegas;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

const normalizeEstablecimientos = (data) => {
  if (Array.isArray(data?.establecimientos)) return data.establecimientos;
  if (Array.isArray(data?.data?.establecimientos)) return data.data.establecimientos;
  return [];
};

const Warehouses = () => {
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [submenuOpen, setSubmenuOpen] = useState(true);

  const [bodegas, setBodegas] = useState([]);
  const [establecimientos, setEstablecimientos] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [establecimientoFilter, setEstablecimientoFilter] = useState("TODOS");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [formModalOpen, setFormModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [snackbar, setSnackbar] = useState({
    open: false,
    type: "success",
    message: "",
  });

  const showSnackbar = (message, type = "success") => {
    setSnackbar({
      open: true,
      type,
      message,
    });
  };

  const fetchBodegas = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/productos/bodegas/`, {
        method: "GET",
        headers: authHeaders(),
        credentials: "include",
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(
          data?.error ||
            data?.detail ||
            "No se pudieron cargar las bodegas."
        );
      }

      setBodegas(normalizeBodegas(data));
      setEstablecimientos(normalizeEstablecimientos(data));
    } catch (error) {
      setBodegas([]);
      setEstablecimientos([]);
      showSnackbar(error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBodegas();
  }, []);

  const filtered = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();

    return bodegas
      .filter((bodega) => {
        const matchesSearch =
          String(bodega.codigo || "").toLowerCase().includes(term) ||
          String(bodega.nombre || "").toLowerCase().includes(term) ||
          String(bodega.establecimiento_codigo || "").toLowerCase().includes(term) ||
          String(bodega.establecimiento_nombre || "").toLowerCase().includes(term);

        const matchesEstablecimiento =
          establecimientoFilter === "TODOS" ||
          String(bodega.establecimiento_id || "") === String(establecimientoFilter);

        return matchesSearch && matchesEstablecimiento;
      })
      .sort((a, b) =>
        String(a.codigo || "").localeCompare(String(b.codigo || ""))
      );
  }, [bodegas, searchTerm, establecimientoFilter]);

  const paginated = useMemo(() => {
    const start = page * rowsPerPage;
    return filtered.slice(start, start + rowsPerPage);
  }, [filtered, page, rowsPerPage]);

  useEffect(() => {
    setPage(0);
  }, [searchTerm, establecimientoFilter]);

  const handleOpenCreate = () => {
    const firstEstablecimiento =
      establecimientos.length > 0 ? establecimientos[0].id : "";

    setSelected(null);
    setForm({
      codigo: "",
      nombre: "",
      establecimiento_id: firstEstablecimiento,
    });
    setFormModalOpen(true);
  };

  const handleOpenEdit = (bodega) => {
    setSelected(bodega);
    setForm({
      codigo: bodega.codigo || "",
      nombre: bodega.nombre || "",
      establecimiento_id: bodega.establecimiento_id || "",
    });
    setFormModalOpen(true);
  };

  const handleCloseForm = () => {
    if (saving) return;
    setFormModalOpen(false);
    setSelected(null);
    setForm(emptyForm);
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const validateForm = () => {
    if (!form.nombre.trim()) {
      showSnackbar("El nombre de la bodega es obligatorio.", "warning");
      return false;
    }

    if (form.codigo && form.codigo.length > 10) {
      showSnackbar("El código no puede tener más de 10 caracteres.", "warning");
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);

    try {
      const payload = {
        codigo: form.codigo.trim(),
        nombre: form.nombre.trim(),
        establecimiento_id: form.establecimiento_id || null,
      };

      const url = selected
        ? `${API_BASE}/productos/bodegas/editar/${selected.id}/`
        : `${API_BASE}/productos/bodegas/crear/`;

      const res = await fetch(url, {
        method: selected ? "PUT" : "POST",
        headers: authHeaders(),
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(
          data?.error ||
            data?.detail ||
            data?.message ||
            "No se pudo guardar la bodega."
        );
      }

      showSnackbar(
        selected
          ? "Bodega actualizada correctamente."
          : "Bodega creada correctamente."
      );

      handleCloseForm();
      fetchBodegas();
    } catch (error) {
      showSnackbar(error.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleOpenDelete = (bodega) => {
    setSelected(bodega);
    setDeleteModalOpen(true);
  };

  const handleCloseDelete = () => {
    if (saving) return;
    setSelected(null);
    setDeleteModalOpen(false);
  };

  const handleDelete = async () => {
    if (!selected) return;

    setSaving(true);

    try {
      const res = await fetch(
        `${API_BASE}/productos/bodegas/eliminar/${selected.id}/`,
        {
          method: "DELETE",
          headers: authHeaders(),
          credentials: "include",
        }
      );

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(
          data?.error ||
            data?.detail ||
            data?.message ||
            "No se pudo eliminar la bodega."
        );
      }

      showSnackbar(data?.message || "Bodega eliminada correctamente.");
      handleCloseDelete();
      fetchBodegas();
    } catch (error) {
      showSnackbar(error.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch("/sistema/logout/", {
        method: "POST",
        credentials: "include",
      });
    } catch {}
    localStorage.clear();
    navigate("/login");
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#f5f6fa" }}>
      <SidebarMenu
        open={open}
        setOpen={setOpen}
        submenuOpen={submenuOpen}
        setSubmenuOpen={setSubmenuOpen}
        handleLogout={handleLogout}
        navigate={navigate}
      />

      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <TopBar open={open} setOpen={setOpen} />

        <Box sx={{ width: "95%", mx: "auto", mt: 3, mb: 5 }}>
          <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 3 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenCreate}
              sx={{
                textTransform: "none",
                backgroundColor: "#1565C0",
                borderRadius: 2,
                px: 2.2,
                fontWeight: 700,
                boxShadow: "0 8px 20px rgba(21,101,192,0.22)",
                "&:hover": { bgcolor: "#0D47A1" },
              }}
            >
              Nueva Bodega
            </Button>
          </Box>

          <Paper
            sx={{
              p: 2.5,
              borderRadius: 3,
              boxShadow: "0 10px 30px rgba(15,23,42,0.06)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: { xs: "flex-start", md: "center" },
                justifyContent: "space-between",
                gap: 2,
                mb: 2,
                flexWrap: "wrap",
              }}
            >
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: 19, color: "#0f172a" }}>
                  Bodegas
                </Typography>
                <Typography sx={{ fontSize: 13, color: "#64748b", mt: 0.4 }}>
                  Inventario organizado por bodega y establecimiento
                </Typography>
              </Box>

              <Stack direction="row" spacing={1} alignItems="center">
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: "#1565C0",
                    border: "1px solid #90caf9",
                    borderRadius: 2,
                    px: 1.2,
                    py: 0.6,
                  }}
                >
                  {filtered.length} registros
                </Typography>

                <Tooltip title="Actualizar" arrow>
                  <IconButton
                    onClick={fetchBodegas}
                    disabled={loading}
                    sx={{
                      p: 0.7,
                      borderRadius: 2,
                      border: "1px solid #e2e8f0",
                      bgcolor: "#fff",
                      "&:hover": { bgcolor: "#f8fafc" },
                    }}
                  >
                    <RefreshIcon sx={{ fontSize: 22 }} />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Box>

            <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 2 }}>
              <TextField
                fullWidth
                size="small"
                label="Buscar por código, nombre o establecimiento"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />

              <TextField
                select
                size="small"
                label="Establecimiento"
                value={establecimientoFilter}
                onChange={(e) => setEstablecimientoFilter(e.target.value)}
                SelectProps={{ native: true }}
                sx={{ minWidth: { xs: "100%", md: 260 } }}
              >
                <option value="TODOS">Todos</option>
                {establecimientos.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.codigo} - {item.nombre}
                  </option>
                ))}
              </TextField>
            </Stack>

            <TableContainer
              sx={{
                borderRadius: 2,
                border: "1px solid #e2e8f0",
                overflowX: "auto",
                overflowY: "hidden",
                maxWidth: "100%",
                "&::-webkit-scrollbar": { height: 10 },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#cbd5e1",
                  borderRadius: 10,
                },
              }}
            >
              <Table size="small" sx={{ minWidth: 820, tableLayout: "auto" }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...tableHeadSx, minWidth: 120 }}>
                      Código
                    </TableCell>
                    <TableCell sx={{ ...tableHeadSx, minWidth: 220 }}>
                      Bodega
                    </TableCell>
                    <TableCell sx={{ ...tableHeadSx, minWidth: 220 }}>
                      Establecimiento
                    </TableCell>
                    <TableCell sx={{ ...tableHeadSx, minWidth: 130 }} align="center">
                      Productos
                    </TableCell>
                    <TableCell sx={{ ...tableHeadSx, minWidth: 130 }} align="center">
                      Stock Total
                    </TableCell>
                    <TableCell sx={{ ...tableHeadSx, minWidth: 130 }} align="center">
                      Acción
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                        <CircularProgress size={26} />
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginated.map((bodega) => (
                      <TableRow
                        key={bodega.id}
                        hover
                        sx={{
                          "& td": { py: 1, borderBottom: "1px solid #edf2f7" },
                          "&:hover": { bgcolor: "#fafcff" },
                        }}
                      >
                        <TableCell>
                          <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#1565C0" }}>
                            {bodega.codigo || "-"}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>
                            {bodega.nombre || "-"}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Stack spacing={0.35}>
                            <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#0f172a" }}>
                              {bodega.establecimiento_nombre || "Sin establecimiento"}
                            </Typography>
                            {!!bodega.establecimiento_codigo && (
                              <Typography sx={{ fontSize: 11.5, color: "#64748b" }}>
                                Código: {bodega.establecimiento_codigo}
                              </Typography>
                            )}
                          </Stack>
                        </TableCell>

                        <TableCell align="center">
                          <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}>
                            {bodega.productos ?? 0}
                          </Typography>
                        </TableCell>

                        <TableCell align="center">
                          <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}>
                            {Number(bodega.stock_total || 0).toFixed(2)}
                          </Typography>
                        </TableCell>

                        <TableCell align="center">
                          <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
                            <Tooltip title="Editar bodega" arrow>
                              <IconButton
                                size="small"
                                onClick={() => handleOpenEdit(bodega)}
                                sx={{
                                  color: "#1565C0",
                                  borderRadius: 2,
                                  border: "1px solid #dbeafe",
                                  bgcolor: "#f8fbff",
                                  "&:hover": { bgcolor: "#eef6ff" },
                                }}
                              >
                                <EditOutlinedIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="Eliminar bodega" arrow>
                              <IconButton
                                size="small"
                                onClick={() => handleOpenDelete(bodega)}
                                sx={{
                                  color: "#d32f2f",
                                  borderRadius: 2,
                                  border: "1px solid #fecaca",
                                  bgcolor: "#fff7f7",
                                  "&:hover": { bgcolor: "#ffefef" },
                                }}
                              >
                                <DeleteOutlineRoundedIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}

                  {!loading && !filtered.length && (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                        <Stack spacing={0.75} alignItems="center">
                          <WarehouseIcon sx={{ fontSize: 40, color: "#94a3b8" }} />
                          <Typography sx={{ fontWeight: 700, color: "#0f172a" }}>
                            No hay bodegas para mostrar
                          </Typography>
                          <Typography sx={{ fontSize: 13, color: "#64748b" }}>
                            Crea una bodega o ajusta los filtros
                          </Typography>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={filtered.length}
              page={page}
              onPageChange={(e, np) => setPage(np)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 20, 25, 50]}
              labelRowsPerPage="Filas por página:"
            />
          </Paper>
        </Box>
      </Box>

      <Modal
        open={formModalOpen}
        onClose={handleCloseForm}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 250 }}
      >
        <Fade in={formModalOpen}>
          <Box sx={modalBoxSx}>
            <Box
              sx={{
                px: 2.2,
                py: 1.6,
                borderBottom: "1px solid #e2e8f0",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Typography sx={{ fontWeight: 700, color: "#0f172a" }}>
                {selected ? "Editar bodega" : "Nueva bodega"}
              </Typography>

              <Button
                onClick={handleCloseForm}
                variant="outlined"
                size="small"
                disabled={saving}
                sx={{ textTransform: "none", fontWeight: 700 }}
              >
                Cerrar
              </Button>
            </Box>

            <Box sx={{ p: 2.5, overflowY: "auto", maxHeight: "calc(90vh - 64px)" }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Código"
                    value={form.codigo}
                    onChange={(e) => handleChange("codigo", e.target.value)}
                    placeholder="Automático"
                    disabled={saving}
                  />
                </Grid>

                <Grid item xs={12} sm={8}>
                  <TextField
                    fullWidth
                    required
                    label="Nombre"
                    value={form.nombre}
                    onChange={(e) => handleChange("nombre", e.target.value)}
                    disabled={saving}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    select
                    SelectProps={{ native: true }}
                    label="Establecimiento"
                    value={form.establecimiento_id}
                    onChange={(e) => handleChange("establecimiento_id", e.target.value)}
                    disabled={saving}
                  >
                    {establecimientos.length === 0 && (
                      <option value="">Sin establecimiento</option>
                    )}

                    {establecimientos.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.codigo} - {item.nombre}
                      </option>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} textAlign="right">
                  <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={saving}
                    startIcon={saving ? <CircularProgress size={18} /> : null}
                    sx={{
                      textTransform: "none",
                      bgcolor: "#1565C0",
                      "&:hover": { bgcolor: "#0D47A1" },
                      borderRadius: 2,
                      px: 2.2,
                      fontWeight: 700,
                      boxShadow: "0 8px 20px rgba(21,101,192,0.22)",
                    }}
                  >
                    Guardar cambios
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Fade>
      </Modal>

      <Modal open={deleteModalOpen} onClose={handleCloseDelete}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "white",
            p: 4,
            borderRadius: 3,
            width: { xs: "92%", sm: 380 },
            boxShadow: "0 20px 60px rgba(15,23,42,0.22)",
          }}
        >
          <Typography variant="h6" mb={1.5} sx={{ fontWeight: 700 }}>
            ¿Eliminar bodega?
          </Typography>

          <Typography mb={3} sx={{ color: "#475569" }}>
            {selected?.nombre
              ? `Se eliminará la bodega "${selected.nombre}".`
              : "Esta acción no se puede deshacer."}
          </Typography>

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button variant="outlined" onClick={handleCloseDelete} disabled={saving}>
              Cancelar
            </Button>
            <Button variant="contained" color="error" onClick={handleDelete} disabled={saving}>
              Eliminar
            </Button>
          </Box>
        </Box>
      </Modal>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.type}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Warehouses;